import { companyLimitedRoleName } from "./CompanyUserRoles.js";
import { isNullOrEmpty } from "../core/helpers/object-util.js";
import CompanyRole from "./CompanyRole.js";
import User from "./User.js";

/**
 * Sub class of user.
 *
 * @param {company[]} spec.companies - List of companies to which the user belong
 * @param {string} spec.agencyId - Identifier of the agency owning the user
 * @return {companyUser}
 */
export default class CompanyUser extends User {
	constructor(spec = {}) {
		super(spec);

		this._companyRoles = {};
		this._agencyId = spec.agencyId;

		if (!isNullOrEmpty(spec.companies) && Array.isArray(spec.companies)) {
			spec.companies.forEach((companySpec) => {
				this._companyRoles[companySpec.companyId] = new CompanyRole(
					companySpec,
				);
			});
		}
	}

	canBeNotifiedFor(entry, company) {
		return entry.canBeAccessedByCompanyUser(this, company);
	}

	getNumberOfCompanies() {
		return Object.keys(this._companyRoles).length;
	}

	getAgencyId() {
		return this._agencyId;
	}

	isCompanyUser() {
		return true;
	}

	getCompanyRoles() {
		return Object.values(this._companyRoles);
	}

	getCompanyIds() {
		return this.getCompanyRoles().map((company) => company.getCompanyId());
	}

	getCompanyRole(company) {
		return this._companyRoles[company.getLegacyId()];
	}

	setCompanyRole(company, typeOfRole) {
		const existingCompanyRole = this.getCompanyRole(company);

		if (existingCompanyRole) {
			existingCompanyRole.setTypeOfRole(typeOfRole);
		} else {
			const id = company.getLegacyId();

			this._companyRoles[id] = new CompanyRole({
				companyId: id,
				typeOfRole,
			});
		}
	}

	addCompany(company, role) {
		return this.setCompanyRole(company, role);
	}

	removeCompany(company) {
		this.removeCompanyRole(company);
	}

	hasCompanyRole(roles, companyId) {
		if (!roles || roles.length === 0) {
			return true;
		}
		const companyRole = this._companyRoles[companyId];
		if (companyRole) {
			return companyRole.hasRole(roles);
		}
		return false;
	}

	removeCompanyRole(company) {
		const companyRole = this.getCompanyRole(company);
		if (companyRole) {
			delete this._companyRoles[company.getLegacyId()];
		}
	}

	roleForCompany(company) {
		return this._companyRoles[company.getLegacyId()].getTypeOfRole();
	}

	// converts from old-style roles to new-style
	getRoles() {
		return this.getCompanyRoles().map((oldRole) =>
			oldRole.toNewStyleRole(this.getAgencyId()),
		);
	}

	canEditLiquidityReport(company) {
		const role = this.roleForCompany(company);
		return role !== companyLimitedRoleName;
	}

	canViewWithdrawnPeriodReports(company) {
		return this.isAdminUser(company.getLegacyId());
	}

	canSeePeriodReportViewers(company) {
		return (
			this.isAdminUser(company.getLegacyId()) && !this.isTemporaryUser()
		);
	}

	canRegisterViewEventForReport() {
		return true;
	}

	_defaultTypeOfUser() {
		return "CompanyUser";
	}

	_serializedProperties() {
		const schema = super._serializedProperties();

		schema.agencyId = this._agencyId;

		return schema;
	}

	static possibleTypes() {
		return ["CompanyUser", "Presentation"];
	}
}
